/**
 * Analytics Event Hooks - Unified tracking for GA4/GTM/Plausible
 * Integrates with GTM dataLayer for Consent Mode V2 compliance
 */
import { pushToDataLayer, GA4Events } from './gtm';

type EventName = 
  | 'cta_book_call_click'
  | 'cta_get_quote_click'
  | 'whatsapp_click'
  | 'form_submit'
  | 'service_card_click'
  | 'language_switch'
  | 'resource_download'
  | 'cookie_consent_saved'
  | 'pricing_package_select'
  | 'package_select'
  | 'pilot_start_click'
  | 'careers_apply_click'
  | 'book_call_click'
  | 'get_quote_click'
  | 'request_security_docs_click'
  | 'security_docs_form_submit'
  | 'career_application_submitted'
  | 'contact_form_submitted';

interface EventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  [key: string]: any;
}

/**
 * Track analytics event - unified tracking via GTM dataLayer
 * Works with GA4, Plausible, or any GTM-configured analytics
 */
export function trackEvent(eventName: EventName, params?: EventParams): void {
  // Push to GTM dataLayer (primary method)
  pushToDataLayer(eventName, {
    event_category: params?.event_category || 'engagement',
    event_label: params?.event_label,
    value: params?.value,
    ...params,
  });

  // Legacy GA4 direct integration (fallback if gtag exists)
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', eventName, {
      event_category: params?.event_category || 'engagement',
      event_label: params?.event_label,
      value: params?.value,
      ...params,
    });
  }

  // Plausible integration
  if (typeof window !== 'undefined' && (window as any).plausible) {
    (window as any).plausible(eventName, { props: params });
  }

  // Console log in development
  if (import.meta.env.DEV) {
    console.log(`[Analytics] ${eventName}`, params);
  }
}

// Convenience functions for specific events
export const trackBookCallClick = (label?: string) => {
  trackEvent('cta_book_call_click', { event_label: label || 'Header CTA' });
  GA4Events.ctaClick('Book Call', label || 'Header');
};

export const trackGetQuoteClick = (label?: string) => {
  trackEvent('cta_get_quote_click', { event_label: label || 'Quote CTA' });
  GA4Events.ctaClick('Get Quote', label || 'CTA');
};

export const trackWhatsAppClick = () => {
  trackEvent('whatsapp_click', { event_category: 'contact' });
  GA4Events.whatsappClick();
};

export const trackFormSubmit = (formName: string, success: boolean = true) => {
  trackEvent('form_submit', { event_label: formName, event_category: 'conversion' });
  GA4Events.formSubmit(formName, success);
  if (success) {
    GA4Events.generateLead({ form_name: formName });
  }
};

export const trackServiceCardClick = (serviceName: string) => {
  trackEvent('service_card_click', { event_label: serviceName, event_category: 'navigation' });
  GA4Events.serviceView(serviceName, 'service_card');
};

export const trackLanguageSwitch = (newLanguage: string) => {
  trackEvent('language_switch', { event_label: newLanguage, event_category: 'preferences' });
};

export const trackPhoneClick = (phoneNumber: string) => {
  GA4Events.phoneClick(phoneNumber);
};

export const trackEmailClick = (emailAddress: string) => {
  GA4Events.emailClick(emailAddress);
};

export const trackFileDownload = (fileName: string, fileType: string) => {
  trackEvent('resource_download', { event_label: fileName, event_category: 'download' });
  GA4Events.fileDownload(fileName, fileType);
};

export const trackPilotRequest = () => {
  trackEvent('pilot_start_click', { event_category: 'conversion' });
  GA4Events.pilotRequest();
};

export const trackQuoteRequest = (serviceName?: string, estimatedValue?: number) => {
  GA4Events.quoteRequest(serviceName, estimatedValue);
};